<template>
  <ToolShell
    tool-name="Booking page generator"
    h1="Free booking page generator: see your page before you sign up."
    lede="A booking page lets clients pick a service and a time and book it themselves. Type your business, what people can book and when, and the real Workspace369 booking page fills in beside the form. When it looks right, start a trial and it goes live in one tap."
    :trust="['Real booking page', 'No signup to try', 'Live with your trial']"
    form-title="Your booking page"
    :upsell-points="[
      'Bookings land in your calendar and on the client record',
      'Confirmation and reminder emails go out for you',
      'Add deposits, intake questions and team members later',
    ]"
    free-label="A booking link on its own"
    :free-bullets="[
      'A separate scheduling tool that knows nothing about your clients',
      'You copy each booking into your calendar and CRM by hand',
      'Invoices and follow-ups live somewhere else',
      'Another monthly bill for one job',
    ]"
    upgrade-headline="A booking page that is part of how you run client work."
    :connected-bullets="[
      'Each booking creates or updates the client record',
      'Bookings sit in the same calendar as your projects and tasks',
      'Proposals, invoices and payment links follow from the booking',
      'Reminder emails cut no-shows without extra tools',
      'Your page lives at 369.dev, ready for your bio, signature or website',
    ]"
    slug="/tools/booking-page-generator"
    :meta-description="metaDescription"
    :doc-brand="draft?.name || 'Your business'"
    doc-subtitle="Booking page"
    doc-kind="BOOKING"
    stamp="Preview"
    doc-label="Your booking page preview"
    :app-href="signupUrl"
    disclosure-plan="specialist"
  >
    <template #form>
      <a href="#bpg-preview" class="bpg-jump">See your booking page <span aria-hidden="true">↓</span></a>
      <label class="ta-field">
        <span>Business name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14V4.5L8 2l6 2.5V14M5.5 14v-4h5v4M2 14h12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input id="bpg-business" v-model="form.businessName" type="text" maxlength="80" placeholder="Harbor & Pine Studio" autocomplete="organization" />
        </div>
      </label>

      <div class="ta-field">
        <span id="bpg-services-label">What can people book?</span>
        <div class="bpg-services" aria-labelledby="bpg-services-label">
          <div v-for="(service, index) in form.services" :key="service.key" class="bpg-service">
            <div class="bpg-service-row">
              <div class="ta-input">
                <input
                  :id="`bpg-service-${index}`"
                  v-model="service.name"
                  type="text"
                  maxlength="80"
                  :placeholder="index === 0 ? 'Intro call' : 'Another service'"
                  :aria-label="`Service ${index + 1} name`"
                />
              </div>
              <button
                v-if="form.services.length > 1"
                type="button"
                class="ta-line-remove"
                :aria-label="`Remove service ${index + 1}`"
                @click="removeService(index)"
              >
                ×
              </button>
            </div>
            <div class="bpg-seg" role="radiogroup" :aria-label="`Service ${index + 1} length`">
              <label
                v-for="minutes in lengths"
                :key="minutes"
              >
                <input
                  v-model.number="service.minutes"
                  class="sr-only"
                  type="radio"
                  :name="`bpg-duration-${service.key}`"
                  :value="minutes"
                />
                <span>{{ minutes }} min</span>
              </label>
            </div>
            <div class="ta-input">
              <span class="bpg-currency" aria-hidden="true">$</span>
              <input
                :id="`bpg-price-${index}`"
                v-model="service.price"
                type="text"
                inputmode="decimal"
                maxlength="9"
                placeholder="Price, optional"
                :aria-label="`Service ${index + 1} price in US dollars, optional`"
              />
            </div>
          </div>
        </div>
        <button v-if="form.services.length < maxServices" type="button" class="ta-add" @click="addService">+ Add another service</button>
      </div>

      <div class="ta-field">
        <span id="bpg-meet-label">How do you meet?</span>
        <div class="bpg-seg" role="radiogroup" aria-labelledby="bpg-meet-label">
          <label
            v-for="option in meetOptions"
            :key="option.value"
          >
            <input v-model="form.meet" class="sr-only" type="radio" name="bpg-meet" :value="option.value" />
            <span>{{ option.label }}</span>
          </label>
        </div>
      </div>

      <div class="ta-field">
        <span id="bpg-hours-label">When can people book?</span>
        <div class="bpg-seg" role="radiogroup" aria-labelledby="bpg-hours-label">
          <label
            v-for="option in hourOptions"
            :key="option.value"
          >
            <input v-model="form.hours" class="sr-only" type="radio" name="bpg-hours" :value="option.value" />
            <span>{{ option.label }}</span>
          </label>
        </div>
      </div>

      <a
        :href="ctaHref"
        class="ta-download bpg-cta"
        :aria-disabled="draft ? undefined : 'true'"
        data-conversion-event="start_free_click"
        data-conversion-label="Booking page generator make it live"
        data-conversion-location="/tools/booking-page-generator"
        @click="onMakeLive"
      >
        Make this page live
      </a>
      <TrialDisclosure plan="specialist" />
      <p class="ta-note" role="status">
        {{ draft ? "Your answers ride along in the sign-up link. Nothing is saved on this site. After the card step, Workspace369 opens your booking page filled in; one tap publishes it." : "Add your business name and at least one service to make the page live." }}
      </p>
    </template>

    <template #preview>
      <BookingPreviewFrame id="bpg-preview" :payload="previewPayload" />
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Describe your bookings</h2><p>Your business name, up to three services with a length and price, how you meet and which days.</p></div></div>
      <div><span>02</span><div><h2>Check the real page</h2><p>The preview is Workspace369's own booking page, the one your clients will see.</p></div></div>
      <div><span>03</span><div><h2>Make it live</h2><p>Start the 14-day trial. Your page opens filled in, and one tap publishes it at 369.dev.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Booking page generator FAQ" :faq="faq" />
    <RelatedPages path="/tools/booking-page-generator" title="Free booking page generator" />
    <Cta03 />
  </ToolShell>
</template>

<script setup>
import BookingPreviewFrame from "~/components/tools/BookingPreviewFrame.vue";
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import {
  BOOKING_DRAFT_LENGTHS,
  BOOKING_DRAFT_LIMITS,
  bookingPreviewPayload,
  bookingSignupUrl,
  buildBookingDraft,
} from "~/utils/booking-page-draft.mjs";
import { toolEvent } from "~/utils/tracking/toolEvents";

const lengths = BOOKING_DRAFT_LENGTHS;
const maxServices = BOOKING_DRAFT_LIMITS.services;
const meetOptions = [
  { value: "video", label: "Video" },
  { value: "phone", label: "Phone" },
  { value: "in_person", label: "In person" },
];
const hourOptions = [
  { value: "weekdays", label: "Mon–Fri, 9–5" },
  { value: "weekdays_saturday", label: "Mon–Sat, 9–5" },
];

let nextKey = 1;
const newService = (minutes = 30) => ({ key: nextKey++, name: "", minutes, price: "" });

const form = reactive({
  businessName: "",
  services: [newService(30)],
  meet: "video",
  hours: "weekdays",
});

const addService = () => {
  if (form.services.length < maxServices) form.services.push(newService(60));
};
const removeService = (index) => {
  if (form.services.length > 1) form.services.splice(index, 1);
};

const draft = computed(() => buildBookingDraft(form));

// Until the visitor types, the preview shows the placeholders as an example.
const exampleDraft = {
  v: 1,
  name: "Harbor & Pine Studio",
  services: [
    { name: "Intro call", minutes: 30, price: 0 },
    { name: "Brand strategy session", minutes: 90, price: 450 },
  ],
  meet: "video",
  hours: "weekdays",
};
// The visitor's own zone labels the preview's times (client-only, so the
// prerendered page stays the same everywhere).
const timeZone = ref("");
onMounted(() => {
  try {
    timeZone.value = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    timeZone.value = "";
  }
});
// The preview follows whatever is typed so far: the typed name or the
// example's, the typed services or the example's.
const previewDraft = computed(() => {
  if (draft.value) return draft.value;
  const withName = { ...form, businessName: form.businessName.trim() || exampleDraft.name };
  return (
    buildBookingDraft(withName) ?? buildBookingDraft({ ...withName, services: exampleDraft.services }) ?? exampleDraft
  );
});
const previewPayload = computed(() => bookingPreviewPayload(previewDraft.value, { timeZone: timeZone.value }));

const { addReferralToUrl } = useReferral();
const signupUrl = computed(() => bookingSignupUrl(draft.value));
const ctaHref = computed(() => addReferralToUrl(signupUrl.value));

function onMakeLive(event) {
  if (draft.value) return;
  event.preventDefault();
  const target = form.businessName.trim() ? "bpg-service-0" : "bpg-business";
  document.getElementById(target)?.focus();
}

// One "use" per visit: the first time the form describes a complete page.
const { consent } = useCookieConsent();
let counted = false;
watch(draft, (value) => {
  if (!value || counted) return;
  const event = toolEvent(consent.value?.analytics === true, "generate", "booking-page-generator");
  if (!event) return;
  counted = true;
  const { event: name, ...parameters } = event;
  if (typeof window.gtag === "function") window.gtag("event", name, parameters);
  else {
    window.dataLayer ||= [];
    window.dataLayer.push(event);
  }
});

const guideSections = [
  {
    title: "What a booking page needs",
    paragraphs: [
      "A client who opens your booking page should be able to answer three questions without writing to you: what can I book, how long is it, and when are you free. The generator asks for exactly that.",
    ],
    bullets: [
      "Business name: the page title, so clients know they are in the right place.",
      "Services: up to three to start. Name each one the way a client would ask for it, such as Intro call or Kitchen design consultation.",
      "Length: 30, 60 or 90 minutes. Pick the time the session really takes, so back-to-back bookings stay realistic.",
      "Price: optional. Leave it empty for a free call; add it when people should know the cost before they book.",
      "How you meet: video, phone or in person. The page tells the client what to expect.",
      "Days: weekdays, or weekdays and Saturday, 9 to 5 in your time zone. You can set exact hours per day later.",
    ],
  },
  {
    title: "Making the page live",
    paragraphs: [
      "Make this page live starts a 14-day Workspace369 trial on the Specialist plan, the plan that includes booking pages. Your answers travel in the sign-up link; this site stores nothing.",
      "After the card step, Workspace369 opens its booking page step with your services, meeting type and days filled in. Check them and tap Create my page. The page is live at once at a 369.dev address you can copy, share or open.",
    ],
  },
  {
    title: "After it is live",
    paragraphs: [
      "Every booking lands in your Workspace369 calendar and on the client record, and the client gets a confirmation email. From the Booking pages screen you can change hours, add services, ask intake questions and embed the page on your own website, and you can take deposits on a service in Products once Stripe is connected.",
    ],
    links: [{ label: "Online booking features", path: "/features/online-booking" }],
  },
];

const faq = [
  {
    q: "Is the booking page generator free?",
    a: "Building and previewing the page is free and needs no account. Publishing it needs a Workspace369 trial: 14 days free on the Specialist plan with a card on file, which you can cancel before the trial ends.",
  },
  {
    q: "Is the preview the real booking page?",
    a: "Yes. The preview is Workspace369's own booking page running in preview mode, the same page your clients will see. In the preview the calendar shows sample weekday times; your live page uses the days you picked.",
  },
  {
    q: "Do you save what I type?",
    a: "No. Nothing is saved on this site. Your answers are carried in the sign-up link, and Workspace369 only uses them to fill in your booking page after you sign up.",
  },
  {
    q: "Can I change the page after it is live?",
    a: "Yes. Services, prices, hours, the page text and its look can all be changed in Workspace369 under Booking pages, and the link stays the same.",
  },
  {
    q: "Why does the trial start on Specialist?",
    a: "Public booking pages are included from the Specialist plan up. Starting the trial there means the page you build here is the page that goes live.",
  },
];

const metaTitle = "Free Booking Page Generator | Workspace369";
const metaDescription =
  "Build a booking page in a minute: add your services, lengths, prices and days, and preview the real page. Make it live with a 14-day Workspace369 trial.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
}));
</script>

<style scoped>
/* Phones stack the preview under the form: a jump link reaches it. */
.bpg-jump {
  display: none;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0e7490;
}
@media (max-width: 959px) {
  .bpg-jump {
    display: inline-block;
  }
}
.dark .bpg-jump {
  color: #67e8f9;
}
.bpg-services {
  display: grid;
  gap: 0.75rem;
}
.bpg-service {
  display: grid;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.bpg-service-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.bpg-service-row .ta-input {
  flex: 1;
  min-width: 0;
}
.bpg-currency {
  color: #64748b;
  font-weight: 600;
}
.bpg-seg {
  display: flex;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}
.bpg-seg label {
  flex: 1 1 0;
  min-width: 0;
  cursor: pointer;
}
.bpg-seg span {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0.5rem 0.35rem;
  white-space: nowrap;
  border: 0;
  background: transparent;
  color: #334155;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
}
.bpg-seg label + label {
  border-left: 1px solid #e2e8f0;
}
.bpg-seg input:checked + span {
  background: #0f172a;
  color: #fff;
}
.bpg-seg input:focus-visible + span {
  outline: 2px solid #0e7490;
  outline-offset: -2px;
}
.bpg-cta {
  text-align: center;
  text-decoration: none;
}
.bpg-cta[aria-disabled="true"] {
  opacity: 0.6;
}
.dark .bpg-service,
.dark .bpg-seg,
.dark .bpg-seg label + label {
  border-color: #243247;
}
.dark .bpg-seg span {
  color: #cbd5e1;
}
.dark .bpg-seg input:checked + span {
  background: #67e8f9;
  color: #020617;
}
.dark .bpg-currency {
  color: #94a3b8;
}
</style>
